import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import type { AgentAccessPerson } from "@/types/agent/agent-access";
import type { EdgeFunctionLog } from "@/types/edge-function-logs";
import { useAgentAccessDiagnosis } from "./hooks/useEdgeFunctionLogs";

const ACCESS_DENIED_PATTERN = /access denied|unauthori[sz]ed|forbidden|do(?:n't| not) have access/i;

const RULE_LABELS: Record<string, string> = {
  admin: "Internal admin",
  owner: "Owner",
  "team-owner": "Team owner's genie",
  teammate: "Teammate",
};

function agentIdFromBody(body: any): string | null {
  const candidates = [body?.data?.agentId, body?.data?.agent_id, body?.agentId, body?.agent_id];
  return candidates.find((value): value is string => typeof value === "string" && value.length > 0) ?? null;
}

export function isAgentAccessDenial(log: EdgeFunctionLog): boolean {
  return log.status === "failed" && !!log.caller_id && !!agentIdFromBody(log.request_body) &&
    ACCESS_DENIED_PATTERN.test(log.error_message ?? "");
}

const PersonRow = ({ person, fallbackId, badge }: {
  person: AgentAccessPerson | null;
  fallbackId?: string;
  badge?: string;
}) => (
  <div className="flex items-start justify-between gap-2 py-2">
    <div className="min-w-0">
      <p className="text-sm font-medium text-genie-text-primary truncate">{person?.full_name || "—"}</p>
      <div className="text-xs text-genie-text-secondary break-all">
        <CopyableEmail email={person?.email ?? null} />
      </div>
      <p className="text-[11px] font-mono text-genie-text-secondary break-all">
        {person?.id ?? fallbackId}
        {person?.team_id != null && ` · team #${person.team_id}`}
        {person?.role && ` · ${person.role}`}
      </p>
    </div>
    {badge && (
      <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-white border border-genie-border text-genie-text-secondary">
        {badge}
      </span>
    )}
  </div>
);

const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
    <p className="text-xs text-genie-text-secondary">{title}</p>
    <div className="divide-y divide-genie-border">{children}</div>
  </div>
);

export const AgentAccessPanel = ({ log }: { log: EdgeFunctionLog }) => {
  const agentId = agentIdFromBody(log.request_body);
  const { data, isLoading, error } = useAgentAccessDiagnosis(agentId, log.caller_id);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-semibold text-genie-text-primary">Access check</h3>
        <p className="text-[11px] text-genie-text-secondary">Current data, not as of the request</p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-genie-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking who can access this genie…
        </div>
      )}

      {error && <p className="text-sm text-red-600">Couldn't load access details: {(error as Error).message}</p>}

      {data && (
        <>
          <div className={`p-3 rounded-lg border flex items-start gap-2 ${
            data.rule ? "bg-green-50 border-green-200 text-green-900" : "bg-red-50 border-red-200 text-red-900"
          }`}>
            {data.rule
              ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              : <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />}
            <p className="text-sm">{data.reason}</p>
          </div>

          <Group title="Caller">
            <PersonRow person={data.caller} fallbackId={log.caller_id ?? undefined}
              badge={data.rule ? RULE_LABELS[data.rule] : "No access"} />
          </Group>

          {data.agent && (
            <Group title={`Genie owner${data.agent.name ? ` · ${data.agent.name}` : ""}`}>
              <PersonRow person={data.owner} fallbackId={data.agent.user_id} badge="Owner" />
            </Group>
          )}

          {data.agent && (
            <Group title={data.team ? `Team #${data.team.id}${data.team.name ? ` · ${data.team.name}` : ""}` : "Team"}>
              {data.members.length
                ? data.members.map((member) => (
                  <PersonRow key={member.id} person={member}
                    badge={member.id === log.caller_id ? "Caller" : undefined} />
                ))
                : <p className="py-2 text-sm text-genie-text-secondary">
                  {data.team ? "No other members" : "The owner has no team"}
                </p>}
            </Group>
          )}

          {data.grants.length > 0 && (
            <Group title="Explicit grants (agent_users)">
              {data.grants.map((grant) => (
                <PersonRow key={grant.user_id} person={grant.user} fallbackId={grant.user_id}
                  badge={grant.expires_at && new Date(grant.expires_at) < new Date()
                    ? "Expired"
                    : grant.user_id === log.caller_id ? "Caller" : undefined} />
              ))}
            </Group>
          )}
        </>
      )}
    </div>
  );
};
